import { ChecklistEvaluationKey } from './ChecklistEvaluations';
import type { EvaluationCoreDescriptor, EvaluationRowResult } from './ChecklistEvaluations';
import { FlagPurposeType } from './Flags';

const DEFAULT_TOLERANCE_DEGREES = 5;

const round = (value: number, decimals = 1): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * One row per design array; on-site measurement (`entered.installedPitch`) is the source of
 * truth, so techs may add/remove rows beyond the design array count. When there's no design
 * pitch to compare against, a row auto-passes once a value is entered rather than staying
 * `incomplete` forever and blocking completion.
 */
export const arrayPitchCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.arrayPitch,
  label: 'Array Pitch',
  repeatable: true,
  supportsDecisionOptions: true,
  flagPurposeType: FlagPurposeType.additionalReview,
  defaultParams: { toleranceDegrees: DEFAULT_TOLERANCE_DEGREES },
  fieldFormats: {
    expectedPitch: { label: 'Expected Pitch', unit: '°', decimals: 1 },
    installedPitch: { label: 'Installed Pitch', unit: '°', decimals: 1 },
  },
  computeRow: (derived, entered, params): EvaluationRowResult => {
    const base = derived.expectedPitch;
    const actual = entered.installedPitch;
    const tolerance = params.toleranceDegrees ?? DEFAULT_TOLERANCE_DEGREES;

    if (actual === null || actual === undefined) {
      return { verdict: 'incomplete' };
    }

    if (params.minDegrees !== undefined && actual < params.minDegrees) {
      return { verdict: 'fail' };
    }
    if (params.maxDegrees !== undefined && actual > params.maxDegrees) {
      return { verdict: 'fail' };
    }

    if (base === null || base === undefined) {
      return { verdict: 'pass' };
    }

    const difference = actual - base;
    const verdict = Math.abs(difference) <= tolerance ? 'pass' : 'fail';

    return {
      verdict,
      difference,
      lowerBound: base - tolerance,
      upperBound: base + tolerance,
    };
  },
  buildFlagReason: (rows, params) => {
    const tolerance = params.toleranceDegrees ?? DEFAULT_TOLERANCE_DEGREES;

    const failingClauses = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.result?.verdict === 'fail')
      .map(({ row, index }) => {
        const installed = row.entered.installedPitch;
        const expected = row.derived.expectedPitch;
        const label = `Array ${index + 1}`;
        if (installed === null || installed === undefined) {
          return `${label} pitch is outside the allowed tolerance of ±${tolerance}°`;
        }
        if (params.minDegrees !== undefined && installed < params.minDegrees) {
          return `${label} installed ${round(installed)}° (below minimum ${params.minDegrees}°)`;
        }
        if (params.maxDegrees !== undefined && installed > params.maxDegrees) {
          return `${label} installed ${round(installed)}° (above maximum ${params.maxDegrees}°)`;
        }
        if (expected === undefined) {
          return `${label} pitch is outside the allowed tolerance of ±${tolerance}°`;
        }
        return `${label} installed ${round(installed)}° vs expected ${round(expected)}° (±${tolerance}°)`;
      });

    if (failingClauses.length === 0) {
      return `Array pitch is outside the allowed tolerance of ±${tolerance}°.`;
    }

    return `${failingClauses.join('; ')}.`;
  },
};
